import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import {
  LogOut,
  User,
  IdCard,
  Play,
  Users,
  Bookmark,
  CreditCard,
  ChevronDown,
  Home,
  Search,
  Vault,
} from "lucide-react";

import Axios from "../../../api/axios/Axios";
import imgLogo from "../../../assets/images/logo_64.png";

import NotificationBell from "../../../features/notification/views/NotificationBell";

import "./DesktopSidebar.css";

const NAV_TOP = [
  { label: "Home", path: "/", icon: Home },
  { label: "Search", path: "/search", icon: Search },
  { label: "Pops", path: "/pops", icon: Play },
  { label: "Notification", type: "notification" },
];

const NAV_SETTINGS_MENU = [
  {
    category: "Features",
    items: [
      { label: "Talents", path: "/talents", icon: IdCard },
      { label: "Groups", path: "/groups", icon: Users },
    ],
  },
  {
    category: "User",
    items: [
      { label: "Profile", path: "/me/profile", icon: User },
      { label: "Talent ID", path: "/me/talents", icon: IdCard },
      { label: "My Rentals", path: "/me/rentals", icon: CreditCard },
      { label: "Saved Media", path: "/me/pops/saved", icon: Bookmark },
      { label: "Creator", path: "/creators", icon: Vault, isCreatorOnly: true },
    ],
  },
  {
    category: null,
    items: [{ label: "Logout", icon: LogOut, variant: "danger" }],
  },
];

const ProfileDropdown = ({ isOpen, handleSignOut, user, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      id="sidebar-profile-menu"
      className="sidebar__dropdown"
      role="group"
      aria-label="Account menu"
    >
      {NAV_SETTINGS_MENU.map((section, index) => {
        const items = section.items.filter(
          (item) => !item.isCreatorOnly || user?.isCreator,
        );

        if (!items.length) return null;

        return (
          <div
            key={section.category || `other-${index}`}
            className="sidebar__dropdown-section"
          >
            {section.category && (
              <div className="sidebar__dropdown-category">
                {section.category}
              </div>
            )}

            <div className="sidebar__dropdown-grid">
              {items.map((item) => {
                const Icon = item.icon;
                const isLogout = item.label === "Logout";

                const className = [
                  "sidebar__dropdown-item",
                  item.variant === "danger"
                    ? "sidebar__dropdown-item--danger"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                const content = (
                  <>
                    <span className="sidebar__dropdown-icon">
                      <Icon size={23} strokeWidth={1.8} />
                    </span>

                    <span className="sidebar__dropdown-label">
                      {item.label}
                    </span>
                  </>
                );

                return isLogout ? (
                  <button
                    key={item.label}
                    type="button"
                    className={className}
                    onClick={handleSignOut}
                  >
                    {content}
                  </button>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={className}
                    onClick={onClose}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function DesktopSidebar({
  isAuthenticated,
  user,
  hasGroupUpdates,
}) {
  const location = useLocation();

  const sidebarRef = useRef(null);
  const profileButtonRef = useRef(null);

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    setShowProfileMenu(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!showProfileMenu) return;

    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowProfileMenu(false);
        profileButtonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showProfileMenu]);

  const handleSignOut = async () => {
    try {
      await Axios.get("/api/sign-out");
      window.location.href = "/";
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const handleToggleProfile = () => {
    setShowProfileMenu((previous) => !previous);
  };

  const handleCloseProfile = () => {
    setShowProfileMenu(false);
  };

  const handleNotificationOpen = () => {
    setShowProfileMenu(false);
  };

  if (!isAuthenticated) return null;

  return (
    <aside className="sidebar" ref={sidebarRef}>
      <div className="sidebar__header">
        <Link to="/" className="sidebar__brand" aria-label="Katch home">
          <img src={imgLogo} alt="Katch" className="sidebar__logo" />
        </Link>
      </div>

      <nav className="sidebar__nav" aria-label="Main navigation">
        <div className="sidebar__group">
          {NAV_TOP.map((item) => {
            if (item.type === "notification") {
              return (
                <NotificationBell
                  key="notification"
                  onOpen={handleNotificationOpen}
                  profileMenuOpen={showProfileMenu}
                />
              );
            }

            const Icon = item.icon;

            return (
              <div key={item.path} className="sidebar__item-wrapper">
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
                  }
                >
                  <span className="sidebar__link-icon">
                    <Icon size={22} strokeWidth={1.8} />

                    {item.label === "Groups" && hasGroupUpdates && (
                      <span className="sidebar__update-dot" />
                    )}
                  </span>

                  <span className="sidebar__label">{item.label}</span>
                </NavLink>
              </div>
            );
          })}
        </div>

        <div className="sidebar__group sidebar__group--footer">
          <div className="sidebar__divider" />

          <div className="sidebar__profile-wrapper">
            <ProfileDropdown
              isOpen={showProfileMenu}
              handleSignOut={handleSignOut}
              user={user}
              onClose={handleCloseProfile}
            />

            <button
              ref={profileButtonRef}
              type="button"
              className={`sidebar__profile-trigger ${
                showProfileMenu ? "sidebar__profile-trigger--active" : ""
              }`}
              onClick={handleToggleProfile}
              aria-label="Account menu"
              aria-haspopup="true"
              aria-expanded={showProfileMenu}
              aria-controls={
                showProfileMenu ? "sidebar-profile-menu" : undefined
              }
            >
              <span
                className={`sidebar__avatar ${
                  showProfileMenu ? "sidebar__avatar--active" : ""
                }`}
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt="" />
                ) : (
                  <User size={21} strokeWidth={1.8} />
                )}
              </span>

              <span className="sidebar__profile-label">You</span>

              <ChevronDown
                size={12}
                className={`sidebar__profile-chevron ${
                  showProfileMenu ? "sidebar__profile-chevron--active" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </nav>
    </aside>
  );
}
